async function saveRate(data) {
  const token =
    localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

  try {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(
      `${import.meta.env.VITE_API_ENDPOINT}/exchange_rate/save`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      }
    );

    return res;
  } catch (error) {}
}

type TodayRateApiResult = {
  rateToEuro: number | null;
  ratedAt: string | null;
};

const todayRateCache = new Map<string, Promise<TodayRateApiResult>>();

function formatDateForRateApi(dateInput: Date): string {
  const year = dateInput.getFullYear();
  const month = String(dateInput.getMonth() + 1).padStart(2, "0");
  const day = String(dateInput.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

async function fetchTodayRateToEuroFromHexa(deviseInput: any): Promise<TodayRateApiResult> {
  const code = String(deviseInput?.code ?? "")
    .trim()
    .toUpperCase();
  if (!code || code === "EUR") {
    return { rateToEuro: code === "EUR" ? 1 : null, ratedAt: null };
  }

  const today = formatDateForRateApi(new Date());
  const cacheKey = `${code}:${today}`;
  const cached = todayRateCache.get(cacheKey);
  if (cached) return cached;

  const promise = (async (): Promise<TodayRateApiResult> => {
    try {
      const res = await fetch(
        `https://api.frankfurter.dev/v2/rates?base=EUR&quotes=${code}&date=${today}`,
      );
      if (!res.ok) return { rateToEuro: null, ratedAt: today };

      const data = await res.json();
      const entries = Array.isArray(data) ? data : [];
      const entry = (entries as any[]).find((e) => e.quote === code);
      const rate = Number(entry?.rate);
      if (!Number.isFinite(rate) || rate <= 0) {
        return { rateToEuro: null, ratedAt: today };
      }

      return { rateToEuro: 1 / rate, ratedAt: today };
    } catch {
      return { rateToEuro: null, ratedAt: today };
    }
  })();

  todayRateCache.set(cacheKey, promise);
  return promise;
}

export async function checkRate(devise) {
  const devises = localStorage.getItem("devises");
  if (devises) {
    const devisesJSON = JSON.parse(devises);
    devise = devisesJSON.filter((d: any) => d.code === devise.code)[0];
  }

  const apiRate = await fetchTodayRateToEuroFromHexa(devise);
  if (
    typeof apiRate.rateToEuro === "number" &&
    Number.isFinite(apiRate.rateToEuro) &&
    apiRate.rateToEuro > 0
  ) {
    return apiRate.rateToEuro;
  }

  if (
    devise?.exchangeRates?.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    ).length > 0
  ) {
    const rate = devise.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    )[0].rate;

    return rate;
  } else {
    const response = {
      meta: {
        last_updated_at: "2024-09-18T23:59:59Z",
      },
      data: {
        EUR: {
          code: "EUR",
          value: 1,
        },
      },
    };

    // if (import.meta.env.VITE_ACTIVE_AMOUNTCONVERTER == 1) {
    //   const currencyApi = new CurrencyAPI(
    //     import.meta.env.VITE_CURRENCYAPI_API_KEY
    //   );

    //   response = await currencyApi.latest({
    //     base_currency: devise.code,
    //     currencies: "EUR",
    //   });
    // }

    if (
      response.data !== undefined &&
      response.data.EUR !== undefined &&
      response.data.EUR.value !== undefined
    ) {
      const data = {
        initialCurrency: devise.code,
        targetCurrency: "EUR",
        rate: response.data.EUR.value,
      };

      const res = await saveRate(data);
      if (res !== null) {
        const devises = await res.json();

        localStorage.setItem("devises", JSON.stringify(devises));
      }

      return response.data.EUR.value;
    }
  }
}

export async function convertToEuro(
  amount: number,
  devise: any,
  ownRate?: any
) {
  if (devise === undefined || devise === null || devise.code === "EUR")
    return amount;
  const devises = localStorage.getItem("devises");
  if (devises) {
    const devisesJSON = JSON.parse(devises);
    devise = devisesJSON.filter((d: any) => d.code === devise.code)[0];
  }

  const ownRateNumber = Number(ownRate);
  if (Number.isFinite(ownRateNumber) && ownRateNumber > 0) {
    return amount * ownRateNumber;
  }
  const apiRateToEuro = (await fetchTodayRateToEuroFromHexa(devise)).rateToEuro;
  if (typeof apiRateToEuro === "number" && apiRateToEuro > 0) {
    return amount * apiRateToEuro;
  }

  if (
    devise?.exchangeRates?.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    ).length > 0
  ) {
    const rate = devise.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    )[0].rate;

    return amount * rate;
  } else {
    const response = {
      meta: {
        last_updated_at: "2024-09-18T23:59:59Z",
      },
      data: {
        EUR: {
          code: "EUR",
          value: 1,
        },
      },
    };

    // if (import.meta.env.VITE_ACTIVE_AMOUNTCONVERTER == 1) {
    //   const currencyApi = new CurrencyAPI(
    //     import.meta.env.VITE_CURRENCYAPI_API_KEY
    //   );

    //   response = await currencyApi.latest({
    //     base_currency: devise.code,
    //     currencies: "EUR",
    //   });
    // }

    if (
      response.data !== undefined &&
      response.data.EUR !== undefined &&
      response.data.EUR.value !== undefined
    ) {
      amount = amount * response.data.EUR.value;
    }

    return amount;
  }
}

export async function convertEuroToCurrency(
  amount: number,
  devise: any,
  ownRate?: any
) {
  if (devise === undefined || devise === null || devise.code === "EUR")
    return amount;

  const devises = localStorage.getItem("devises");
  if (devises) {
    const devisesJSON = JSON.parse(devises);
    devise = devisesJSON.filter((d: any) => d.code === devise.code)[0];
  }

  const ownRateNumber = Number(ownRate);
  if (Number.isFinite(ownRateNumber) && ownRateNumber > 0) {
    return amount / ownRateNumber;
  }
  const apiRateToEuro = (await fetchTodayRateToEuroFromHexa(devise)).rateToEuro;
  if (typeof apiRateToEuro === "number" && apiRateToEuro > 0) {
    return amount / apiRateToEuro;
  }

  if (
    devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    ).length > 0
  ) {
    const rate = devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    )[0].rate;

    return amount / rate;
  } else {
    const response = {
      meta: {
        last_updated_at: "2024-09-18T23:59:59Z",
      },
      data: {
        EUR: {
          code: "EUR",
          value: 1,
        },
      },
    };

    // if (import.meta.env.VITE_ACTIVE_AMOUNTCONVERTER == 1) {
    //   const currencyApi = new CurrencyAPI(
    //     import.meta.env.VITE_CURRENCYAPI_API_KEY
    //   );

    //   response = await currencyApi.latest({
    //     base_currency: devise?.code,
    //     currencies: "EUR",
    //   });
    // }

    if (
      response.data !== undefined &&
      response.data.EUR !== undefined &&
      response.data.EUR.value !== undefined
    ) {
      amount = amount / response.data.EUR.value;
    }

    return amount;
  }
}

export function getCurrentRateInfo(deviseInput: any) {
  if (!deviseInput) return null;

  const inputCode = String(deviseInput?.code ?? "").toUpperCase();
  if (!inputCode || inputCode === "EUR") return null;

  let devise = deviseInput;
  try {
    const storedDevises = localStorage.getItem("devises");
    if (storedDevises) {
      const parsed = JSON.parse(storedDevises);
      if (Array.isArray(parsed)) {
        const found = parsed.find(
          (d: any) => String(d?.code ?? "").toUpperCase() === inputCode
        );
        if (found) devise = found;
      }
    }
  } catch (_) {}

  const exchangeRates = Array.isArray(devise?.exchangeRates)
    ? devise.exchangeRates
    : [];
  const toEur =
    exchangeRates.find(
      (rate: any) =>
        String(rate?.targetCurrency?.code ?? "").toUpperCase() === "EUR"
    ) || exchangeRates[0];

  const rateToEuro = Number(toEur?.rate);
  if (!rateToEuro || Number.isNaN(rateToEuro)) return null;

  return {
    rateToEuro,
    currencyCode: inputCode,
    currencySymbol:
      devise?.symbol ||
      deviseInput?.symbol ||
      devise?.symbolNative ||
      deviseInput?.symbolNative ||
      inputCode,
    ratedAt: toEur?.ratedAt
      ? String(toEur.ratedAt)
      : toEur?.updatedAt
        ? String(toEur.updatedAt)
        : toEur?.createdAt
          ? String(toEur.createdAt)
          : null,
  };
}

export function formatRateDateLabel(ratedAt: any): string | null {
  if (!ratedAt) return null;
  const raw = String(ratedAt);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  return d.toLocaleDateString("fr-FR");
}
